import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import axiosInstance from "./api/axios";
import { FaArrowLeft } from "react-icons/fa";
import { useAuth } from "./AuthContext";

export default function TwoFactorSetup() {
  const [qrData, setQrData] = useState(null);
  const { setupToken, setRequiresPasswordChange } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [otp, setOtp] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const username = localStorage.getItem('username');
  const navigate = useNavigate();

  useEffect(() => {
    if (username && setupToken) {
      axiosInstance.get(`/api/2fa/setup`, {
        // Pass the setup token in the header for this specific request
        headers: { 'Authorization': `Bearer ${setupToken}` }
      })
        .then((res) => {
          setQrData(res.data);
          setLoading(false); // Stop loading on success
        })
        .catch((error) => {
          // --- TIMEOUT FIX: Prevent error flash ---
          if (error.response?.status === 401) return;

          console.error("Error fetching QR data:", error);
          const detail = error.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : "Could not generate setup code. Please try logging in again.");
          setLoading(false); // Stop loading on actual error
        });
    } else {
      navigate('/');
    }
  }, [username, navigate, setupToken]);

  const finish2faSetup = async (event) => {
    event.preventDefault();
    setSaveError(null);
    if (!/^\d{6}$/.test(otp)) {
      setSaveError("Enter the 6-digit code your authenticator app shows for Wysupp Validate.");
      return;
    }
    setSaving(true);
    try {
      // The code proves the app really holds the secret: the server switches
      // 2FA on only then, so a mis-scanned QR code can no longer lock the
      // account. The secret goes back inside the signed enrollment token, not
      // as plain input - the server only accepts a secret it generated.
      const res = await axiosInstance.put(`/api/2fa/setup/save-secret`, {
        enrollment_token: qrData.enrollment_token,
        otp,
      }, {
        headers: { 'Authorization': `Bearer ${setupToken}` }
      });
      if (res.data.password_change_required) {
        // A first login: replace the temporary password next. The flag is set
        // before navigating so the change-password page never mounts without it.
        setRequiresPasswordChange(true);
        navigate('/change-password');
      } else {
        navigate('/', { state: { notice: "Two-factor authentication is set up. Sign in with your password and a code from your app." } });
      }
    } catch (error) {
      // --- TIMEOUT FIX: Prevent Zombie Alert ---
      if (error.response?.status === 401) return;

      console.error("Error saving 2FA secret:", error);
      const detail = error.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : "Failed to save your 2FA setup. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="auth-page-container">
      <header className="auth-header">
        <button onClick={() => navigate('/')} className="back-button">
          <FaArrowLeft /> Back to Login
        </button>
        <img src={logo} alt="Wysupp Logo" className="header-logo" />
      </header>

      <main className="auth-card-wrapper">
        <h1 className="card-title">Two-Factor Authentication</h1>
        <p className="card-subtitle">For added security, please set up 2FA using your preferred authenticator app. (e.g. Microsoft Authenticator)</p>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Generating secure code...</p>
          </div>
        ) : error ? (
          <div className="feedback-message error">{error}</div>
        ) : (
          <>
            <div className="qr-code-container">
              <img src={`data:image/png;base64,${qrData.qr_code_base64}`} alt="2FA QR Code" />
            </div>
            <p className="manual-setup-text">Or enter this key manually:</p>
            <div className="secret-key-box">
              {qrData.secret}
            </div>
            <form onSubmit={finish2faSetup} className="verify-form">
              <label htmlFor="setup-otp" className="manual-setup-text">
                Then enter the 6-digit code your app shows, to confirm it is set up:
              </label>
              <input
                id="setup-otp"
                name="otp"
                className="otp-input"
                type="text"
                value={otp}
                /* Digits only: a pasted code often arrives as "123 456". */
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-Digit Code"
                maxLength="6"
                autoComplete="one-time-code"
                inputMode="numeric"
                required
              />
              {saveError && <div className="feedback-message error">{saveError}</div>}
              <button type="submit" className="action-button" disabled={saving}>
                {saving ? 'Verifying...' : 'Verify and Continue'}
              </button>
            </form>
          </>
        )}
      </main>
      <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0;
                    --light-text: #a0aec0; --green-accent: #48bb78; --border-color: #4a5568;
                    --input-bg: #1a202c; --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1);
                }
                .auth-page-container {
                    box-sizing: border-box;
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center; /* This vertically centers the content */
                    padding: 1rem; /* Reduced from 2rem */
                }
                .auth-header {
                    width: 100%;
                    max-width: 500px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }
                .header-logo { height: 30px; }
                .back-button {
                    background: transparent; color: var(--light-text); border: none; padding: 0;
                    font-weight: 600; cursor: pointer; transition: color 0.2s;
                    display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { color: var(--text-color); }
                
                .auth-card-wrapper {
                    width: 100%;
                    max-width: 500px;
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    padding: 2rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }
                .card-title {
                    font-size: 1.75rem; font-weight: 700;
                    color: var(--text-color); margin-bottom: 0.5rem;
                }
                .card-subtitle {
                    font-size: 1rem; color: var(--light-text);
                    margin-bottom: 1.5rem; max-width: 400px;
                }
                
                .qr-code-container {
                    background-color: white;
                    padding: 1rem;
                    border-radius: 8px;
                     margin-bottom: 1rem;
                    display: inline-block;
                }
                .qr-code-container img {
                    display: block;
                    width: 200px;
                    height: 200px;
                }

                .manual-setup-text {
                    color: var(--light-text);
                    font-size: 0.9rem;
                    margin-bottom: 0.5rem;
                }
                .secret-key-box {
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    padding: 0.75rem 1rem;
                    border-radius: 6px;
                    font-family: monospace;
                    letter-spacing: 2px;
                    color: var(--text-color);
                    margin-bottom: 1.5rem;
                }
                
                .action-button {
                    width: 100%;
                    background-color: var(--green-accent); color: var(--dark-bg);
                    border: none; border-radius: 6px; padding: 0.8rem;
                    font-size: 1rem; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s;
                }
                .action-button:hover { background-color: #38a169; }
                .action-button:disabled { opacity: 0.6; cursor: not-allowed; }

                .verify-form {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .otp-input {
                    width: 100%;
                    box-sizing: border-box;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    padding: 0.75rem 1rem;
                    color: var(--text-color);
                    font-family: monospace;
                    font-size: 1.25rem;
                    letter-spacing: 6px;
                    text-align: center;
                }
                .otp-input:focus { outline: none; border-color: var(--green-accent); }

                .feedback-message {
                    width: 100%; padding: 1rem; border-radius: 6px;
                    font-weight: 500; text-align: center;
                }
                .feedback-message.error { background-color: var(--error-bg); color: var(--error-color); }

                .loading-state { text-align: center; color: var(--text-color); padding-top: 5rem; }
                .loading-spinner {
                    width: 40px; height: 40px; margin: 0 auto 1rem;
                    border: 4px solid var(--border-color); border-top-color: var(--green-accent);
                    border-radius: 50%; animation: spin 1s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
    </div>
  );
}